import { ActionMenuPortal } from "@/components/ActionMenuPortal";
import { EmpresaParceiraFormFields } from "@/components/EmpresaParceiraFormFields";
import { ModalPrimaryAction } from "@/components/ModalPrimaryAction";
import { StandardModal } from "@/components/StandardModal";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import {
  type EmpresaParceira,
  type EmpresaParceiraForm,
  EMPTY_EMPRESA_PARCEIRA_FORM,
  empresaParceiraFormToPayload,
  empresaParceiraFormsEqual,
  empresaParceiraToForm,
  formatCnpjDisplay,
  formatPhoneDisplay,
  validateEmpresaParceiraForm,
} from "@/lib/empresaParceiraFields";
import { formatDisplayText } from "@/lib/formatDisplayText";
import { PAGE_TITLE_CLASS } from "@/lib/modalStyles";
import { toast } from "@/lib/appToast";
import { PageContentLoader } from "@/components/PageContentLoader";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { buildFormDraftKey, clearFormDraft, loadFormDraft, saveFormDraft } from "@/lib/formDraft";

const TABLE_HEAD_CLASS = "bg-muted/40 text-muted-foreground";
const TABLE_TH_CLASS = "font-medium";

type Props = {
  active: boolean;
};

export function ParceirosTab({ active }: Props) {
  const [lista, setLista] = useState<EmpresaParceira[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionMenu, setActionMenu] = useState<{ id: number; rect: DOMRect } | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<EmpresaParceiraForm>(EMPTY_EMPRESA_PARCEIRA_FORM);
  const [editFormOriginal, setEditFormOriginal] = useState<EmpresaParceiraForm | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const editDraftKey = useMemo(
    () => (editId != null ? buildFormDraftKey("empresa-parceira", editId) : null),
    [editId],
  );

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await api.listarEmpresasParceiras();
      setLista(rows);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao carregar parceiros");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (active) void carregar();
  }, [active, carregar]);

  const abrirEditar = async (id: number) => {
    setActionMenu(null);
    try {
      const empresa = await api.buscarEmpresaParceira(id);
      const form = empresaParceiraToForm(empresa);
      setEditId(empresa.id);
      setEditForm(loadFormDraft<EmpresaParceiraForm>(buildFormDraftKey("empresa-parceira", empresa.id)) ?? form);
      setEditFormOriginal(form);
      setEditOpen(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao carregar cadastro");
    }
  };

  const hasChanges = useMemo(() => {
    if (!editFormOriginal) return false;
    return !empresaParceiraFormsEqual(editForm, editFormOriginal);
  }, [editForm, editFormOriginal]);

  const canSave = editFormOriginal !== null && validateEmpresaParceiraForm(editForm) === null;
  const showAction = canSave && hasChanges;

  useEffect(() => {
    if (!editOpen || !editDraftKey) return;
    if (!hasChanges) {
      clearFormDraft(editDraftKey, { suppressAutosave: false });
      return;
    }
    saveFormDraft(editDraftKey, editForm);
  }, [editOpen, editDraftKey, editForm, hasChanges]);

  const salvarEdicao = async () => {
    if (editId == null || !showAction) return;
    const err = validateEmpresaParceiraForm(editForm);
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      await api.atualizarEmpresaParceira(editId, empresaParceiraFormToPayload(editForm));
      clearFormDraft(buildFormDraftKey("empresa-parceira", editId));
      setEditOpen(false);
      setEditId(null);
      setEditForm(EMPTY_EMPRESA_PARCEIRA_FORM);
      setEditFormOriginal(null);
      toast.success("Cadastro atualizado com sucesso");
      await carregar();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const excluir = async (id: number) => {
    setActionMenu(null);
    if (!window.confirm("Excluir este cadastro de empresa parceira?")) return;
    try {
      await api.excluirEmpresaParceira(id);
      clearFormDraft(buildFormDraftKey("empresa-parceira", id));
      toast.success("Cadastro excluído");
      await carregar();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao excluir");
    }
  };

  const fecharEdicao = () => {
    if (saving) return;
    if (editDraftKey) clearFormDraft(editDraftKey);
    setEditOpen(false);
    setEditId(null);
    setEditForm(EMPTY_EMPRESA_PARCEIRA_FORM);
    setEditFormOriginal(null);
  };

  return (
    <div className="space-y-4">
      {loading ? (
        <PageContentLoader />
      ) : (
      <>
      <h2 className={PAGE_TITLE_CLASS}>Empresas</h2>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {lista.length === 0 ? (
          <p className="px-4 py-12 text-center text-muted-foreground">Nenhum cadastro de empresa parceira.</p>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] table-fixed text-sm">
            <thead className={TABLE_HEAD_CLASS}>
              <tr className="border-b border-gray-200">
                <th className={`w-[38%] whitespace-nowrap py-3 px-4 text-left ${TABLE_TH_CLASS}`}>Nome da empresa</th>
                <th className={`w-[24%] whitespace-nowrap py-3 px-4 text-left ${TABLE_TH_CLASS}`}>CNPJ</th>
                <th className={`w-[30%] whitespace-nowrap py-3 px-4 text-left ${TABLE_TH_CLASS}`}>WhatsApp do responsável</th>
                <th className={`w-[8%] min-w-[72px] whitespace-nowrap py-3 px-4 text-right ${TABLE_TH_CLASS}`}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((row) => (
                <tr key={row.id} className="border-b border-gray-100 hover:bg-muted/20">
                  <td className="truncate py-2 px-4 font-medium">{formatDisplayText(row.nome_empresa)}</td>
                  <td className="truncate py-2 px-4 text-muted-foreground">{formatCnpjDisplay(row.cnpj)}</td>
                  <td className="truncate py-2 px-4 text-muted-foreground">
                    {formatPhoneDisplay(row.responsavel_whatsapp)}
                  </td>
                  <td className="py-2 px-4 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      aria-label={`Ações de ${row.nome_empresa}`}
                      onClick={(e) => {
                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        setActionMenu((cur) => (cur?.id === row.id ? null : { id: row.id, rect }));
                      }}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      </>
      )}

      {actionMenu ? (
        (() => {
          const row = lista.find((x) => x.id === actionMenu.id);
          if (!row) return null;
          return (
            <ActionMenuPortal
              open
              anchorRect={actionMenu.rect}
              onClose={() => setActionMenu(null)}
            >
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                onClick={() => void abrirEditar(row.id)}
              >
                <Pencil className="h-4 w-4" /> Editar
              </button>
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-destructive hover:bg-destructive/10"
                onClick={() => void excluir(row.id)}
              >
                <Trash2 className="h-4 w-4" /> Excluir
              </button>
            </ActionMenuPortal>
          );
        })()
      ) : null}

      <StandardModal
        open={editOpen}
        title="Editar empresa parceira"
        onClose={fecharEdicao}
        closeDisabled={saving}
        panelClassName="gs-modal-panel-max max-w-xl max-h-[min(90vh,720px)] !overflow-hidden"
      >
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <div className="space-y-4 p-6 pt-4 text-gray-900">
            <EmpresaParceiraFormFields
              form={editForm}
              onChange={(patch) => setEditForm((prev) => ({ ...prev, ...patch }))}
              idPrefix="edit"
            />
          </div>
        </div>
        <div className="shrink-0 bg-white px-6 pb-6">
          <ModalPrimaryAction
            show={showAction}
            loading={saving}
            disabled={!canSave}
            onClick={() => void salvarEdicao()}
          >
            Salvar
          </ModalPrimaryAction>
        </div>
      </StandardModal>
    </div>
  );
}
